import React from "react";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import SubContainer from "./SubContainer";
import { getCurrencySymbol, roundIfDecimal } from "../../../Utils/Utils";

const DayWisePerformanceTable = ({
  tableData,
  isLoading,
  doComparison,
  comparisonTableData,
}) => {
  const hotels = useSelector((state) => state.auth.hotels);
  const hotelCurrency = hotels[0]?.hotelCurrency || "INR";
  const currencySymbol = getCurrencySymbol(hotelCurrency);

  const handleDownloadCSV = () => {
    if (!tableData?.length) return;

    const headers = ["DATE", "REVENUE", "OCCUPANCY", "ADR", "REVPAR"];
    const rows = tableData.map((row) => [
      row.date,
      roundIfDecimal(row.revenue, 2),
      roundIfDecimal(row.occupancy, 0),
      roundIfDecimal(row.adr, 2),
      roundIfDecimal(row.revPAR, 2),
    ]);

    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "day_wise_performance.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <Box height={"600px"}>
        <Skeleton
          variant="rounded"
          width={"100%"}
          height={600}
          animation="wave"
          sx={{
            borderRadius: "8px",
            "&.MuiSkeleton-root": {
              backgroundColor: "#b6bed350",
            },
          }}
        />
      </Box>
    );
  }

  return (
    <SubContainer spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={700} color={"white"}>
          Day-wise Performance Report
        </Typography>

        <Box
          sx={{
            backgroundColor: "#1F2937",
            border: "1px solid #4B5563",
            color: "white",
            fontWeight: "500",
            padding: "0.5rem 1rem",
            borderRadius: "0.5rem",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={handleDownloadCSV}
        >
          <DownloadIcon sx={{ mr: 1 }} />
          Download Report
        </Box>
      </Stack>

      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        {/* HEADER */}
        <Box
          component="thead"
          sx={{
            fontSize: "0.75rem",
            color: "#D1D5DB",
            textTransform: "uppercase",
            backgroundColor: "#1F2937",
          }}
        >
          <Box component="tr">
            <Typography
              component="th"
              sx={{
                padding: "0.75rem 1.5rem",
                textAlign: "left",
                borderTopLeftRadius: "0.5rem",
                borderBottomLeftRadius: "0.5rem",
              }}
            >
              DATE
            </Typography>

            {["REVENUE", "OCCUPANCY", "ADR", "REVPAR"].map((header) => (
              <Box
                key={header}
                component="th"
                sx={{
                  padding: "0.75rem 1.5rem",
                  textAlign: "center",
                }}
              >
                {doComparison ? (
                  <Stack spacing={0.5} alignItems="center">
                    <Typography color="#D1D5DB">{header}</Typography>
                    <Stack direction="row" spacing={4}>
                      <Typography color="#9CA3AF">
                        {new Date().getFullYear()}
                      </Typography>
                      <Typography color="#9CA3AF">
                        {new Date().getFullYear() - 1}
                      </Typography>
                    </Stack>
                  </Stack>
                ) : (
                  <Typography color="#D1D5DB">{header}</Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* BODY */}
        <Box component="tbody">
          {tableData.map((row, i) => {
            const compRow = comparisonTableData?.[i];
            return (
              <Box
                component="tr"
                key={row.date}
                sx={{
                  borderBottom: "1px solid #1F2937",
                  "& td": {
                    borderTop: "none",
                    textAlign: "center", // center align all cells
                    verticalAlign: "middle",
                  },
                }}
              >
                <Box
                  component="td"
                  sx={{
                    py: 2,
                    px: 2,
                    color: "white",
                    textAlign: "left !important", // keep date left aligned
                  }}
                >
                  {row.date}
                </Box>

                {/* Revenue */}
                <Box component="td" sx={{ py: 2, px: 2, color: "#b6bed3" }}>
                  {doComparison ? (
                    <Stack direction="row" spacing={4} justifyContent="center">
                      <span>
                        {currencySymbol +
                          roundIfDecimal(row.revenue, 2)?.toLocaleString()}
                      </span>
                      <span style={{ color: "#9CA3AF" }}>
                        {currencySymbol +
                          roundIfDecimal(compRow?.revenue, 2)?.toLocaleString()}
                      </span>
                    </Stack>
                  ) : (
                    currencySymbol +
                    roundIfDecimal(row.revenue, 2)?.toLocaleString()
                  )}
                </Box>

                {/* Occupancy */}
                <Box component="td" sx={{ py: 2, px: 2, color: "#b6bed3" }}>
                  {doComparison ? (
                    <Stack direction="row" spacing={4} justifyContent="center">
                      <span>{roundIfDecimal(row.occupancy, 0)}%</span>
                      <span style={{ color: "#9CA3AF" }}>
                        {roundIfDecimal(compRow?.occupancy, 0)}%
                      </span>
                    </Stack>
                  ) : (
                    roundIfDecimal(row.occupancy, 0) + "%"
                  )}
                </Box>

                {/* ADR */}
                <Box component="td" sx={{ py: 2, px: 2, color: "#b6bed3" }}>
                  {doComparison ? (
                    <Stack direction="row" spacing={4} justifyContent="center">
                      <span>
                        {currencySymbol +
                          roundIfDecimal(row.adr, 2)?.toLocaleString()}
                      </span>
                      <span style={{ color: "#9CA3AF" }}>
                        {currencySymbol +
                          roundIfDecimal(compRow?.adr, 2)?.toLocaleString()}
                      </span>
                    </Stack>
                  ) : (
                    currencySymbol +
                    roundIfDecimal(row.adr, 2)?.toLocaleString()
                  )}
                </Box>

                {/* RevPAR */}
                <Box component="td" sx={{ py: 2, px: 2, color: "#b6bed3" }}>
                  {doComparison ? (
                    <Stack direction="row" spacing={4} justifyContent="center">
                      <span>
                        {currencySymbol +
                          roundIfDecimal(row.revPAR, 2)?.toLocaleString()}
                      </span>
                      <span style={{ color: "#9CA3AF" }}>
                        {currencySymbol +
                          roundIfDecimal(compRow?.revPAR, 2)?.toLocaleString()}
                      </span>
                    </Stack>
                  ) : (
                    currencySymbol +
                    roundIfDecimal(row.revPAR, 2)?.toLocaleString()
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </SubContainer>
  );
};

export default DayWisePerformanceTable;
